import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useQuill } from "react-quilljs";
import Quill from "quill";
import { v4 } from "uuid";
import ImageResize from "quill-image-resize-module-react";
import "quill/dist/quill.snow.css";
import "./TextEditor-2.scss";

Quill.register("modules/imageResize", ImageResize);

const defaultValidation = {
  minChar: { message: "", condition: "", length: "" },
  maxChar: { message: "", condition: "", length: "" },
  alphabetsOnly: { message: "", condition: "", more: "" },
  alphaNumeric: { message: "", condition: "", more: "" },
};

const TextEditorjs = forwardRef((props, ref) => {
  const {
    label,
    value,
    onChange,
    field,
    placeholder,
    inputSocket,
    className,
    error,
    required,
    validation = defaultValidation,
    condition,
    clearOnHide,
    disabled,
    lang,
    dontTranslate,
    style,
    onBlur,
    config,
    toolbar,
    uploadImageHandler,
  } = props;

  const uuid = useRef(v4());
  const [inValue, setInValue] = useState("");
  const [inError, setInError] = useState("");
  const didSetupRef = useRef(false);

  const { quill, quillRef } = useQuill({
    theme: "snow",
    modules: {
      toolbar: {
        container: toolbar || [
          ["bold", "italic", "underline", "strike"],
          ["blockquote", "code-block"],
          [{ header: 1 }, { header: 2 }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ indent: "-1" }, { indent: "+1" }],
          [{ size: ["small", false, "large", "huge"] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          [{ color: [] }, { background: [] }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["clean"],
          // ["table"],
        ],
      },
      imageResize: {
        parchment: Quill.import("parchment"),
        modules: ["Resize", "DisplaySize"],
      },
    },
    formats: [
      "bold",
      "italic",
      "underline",
      "strike",
      "blockquote",
      "code-block",
      "header",
      "list",
      "indent",
      "size",
      "color",
      "background",
      "align",
      "link",
      "image",
      "video",
      // "table",
    ],
  });

  // Insert uploaded image into the editor
  const insertToEditor = (url) => {
    const range = quill.getSelection();
    quill.insertEmbed(range?.index || 0, "image", url);
  };

  // Trigger image upload and insertion
  const selectLocalImage = async () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      const file = input.files[0];
      if (!file || !uploadImageHandler) return;
      try {
        const url = await uploadImageHandler(file);
        insertToEditor(url);
      } catch (err) {
        setInError("Image upload failed.");
      }
    };
  };

  // Provide Quill instance to parent
  useImperativeHandle(ref, () => ({
    getEditor: () => quill,
  }));

  // Initial value load and event listeners
  useEffect(() => {
    if (!quill || didSetupRef.current) return;

    didSetupRef.current = true;

    let content = "";
    if (typeof value === "object" && field in value) {
      content = value[field];
    } else if (typeof value === "string") {
      content = value;
    }

    setInValue(content);
    quill.root.innerHTML = content || "";

    quill.on("text-change", () => {
      const htmlContent = quill.root.innerHTML;
      const plainText = quill.getText().trim();

      if (
        validation?.maxChar?.length &&
        plainText.length > validation.maxChar.length
      ) {
        setInError(
          validation.maxChar.message ||
            `Maximum ${validation.maxChar.length} characters allowed.`
        );
        return;
      }

      setInValue(htmlContent);
      setInError("");
      onChange && onChange(htmlContent, field);
    });

    quill.on("selection-change", (range, oldRange) => {
      if (range === null && oldRange !== null) {
        const htmlContent = quill.root.innerHTML;
        const plainText = quill.getText().trim();

        if (
          required &&
          (typeof required === "object" ? required?.condition ?? true : true) &&
          plainText.length === 0
        ) {
          setInError(required.message || "This field is required.");
        } else if (
          validation?.minChar?.length &&
          plainText.length < validation.minChar.length
        ) {
          setInError(
            validation.minChar.message ||
              `Minimum ${validation.minChar.length} characters required.`
          );
        } else {
          setInError("");
        }

        onBlur && onBlur(htmlContent);
      }
    });

    quill.getModule("toolbar").addHandler("image", selectLocalImage);
  }, [quill]);

  function ifObjectExist(obj = {}) {
    return Object.values(obj).join("").length > 0 || obj === true;
  }

  useEffect(() => {
    if (condition === undefined || condition === true) {
      let obj = { key: field };
      if (required) obj.required = required;
      if (ifObjectExist(validation.minChar)) obj.minChar = validation.minChar;
      if (ifObjectExist(validation.maxChar)) obj.maxChar = validation.maxChar;
      if (ifObjectExist(validation.alphabetsOnly))
        obj.alphabetsOnly = validation.alphabetsOnly;
      if (ifObjectExist(validation.alphaNumeric))
        obj.alphaNumeric = validation.alphaNumeric;
      inputSocket && inputSocket(obj);
      if (Object.keys(obj).length > 1 && !inputSocket)
        setInError("Please connect 'formPlug' to 'inputSocket'");
    }
    return () => {
      inputSocket && inputSocket({ key: field, clearValidation: true });
    };
  }, [
    ...(required && typeof required === "object"
      ? Object.values(required)
      : [required]),
    condition,
  ]);
  // Cleanup on hide
  useEffect(() => {
    return () => {
      if (condition && clearOnHide && inputSocket) {
        inputSocket({ key: field, clearValidation: true, clearValue: true });
      }
    };
  }, [condition]);

  if (!condition && condition !== undefined) return null;

  const renderError =
    inError || (typeof error === "object" ? error?.[field] : error);

  return (
    <div id="TextEditor2" className={className} style={style}>
      {label && (
        <label htmlFor={uuid.current}>
          {label}
          {(required?.condition !== undefined
            ? required.condition
            : required) && <em>*</em>}
        </label>
      )}
      <div ref={quillRef} />
      {renderError && <small className="error">{renderError}</small>}
    </div>
  );
});

export default TextEditorjs;
